// Draws a health bar above a unit. Internal lerp smooths the visible value toward
// the unit's true currentHp so damage tick is pleasant rather than snapping.

import { effectiveStats } from "../units/UnitStats";
import type { Unit } from "../units/Unit";

const BAR_WIDTH = 48;
const BAR_HEIGHT = 5;
const BAR_OFFSET_Y = -34; // above unit center

export class HealthBar {
  private displayedHp: number;
  private pinned = false;

  constructor(private unit: Unit) {
    this.displayedHp = unit.currentHp;
  }

  pinAt(hp: number): void {
    this.displayedHp = hp;
    this.pinned = true;
  }

  unpin(): void {
    this.pinned = false;
  }

  update(dt: number): void {
    if (this.pinned) return;
    const target = this.unit.currentHp;
    const diff = target - this.displayedHp;
    if (Math.abs(diff) < 0.5) {
      this.displayedHp = target;
      return;
    }
    // Smooth towards target: approx 15% of diff per 16ms frame.
    const factor = 1 - Math.pow(0.85, dt / 16);
    this.displayedHp += diff * factor;
  }

  draw(ctx: CanvasRenderingContext2D, centerX: number, centerY: number): void {
    const maxHp = effectiveStats(this.unit).maxHp;
    const ratio = Math.max(0, Math.min(1, this.displayedHp / maxHp));
    const x = centerX - BAR_WIDTH / 2;
    const y = centerY + BAR_OFFSET_Y;

    ctx.save();
    // Background
    ctx.fillStyle = "#000";
    ctx.globalAlpha = 0.8;
    ctx.fillRect(x - 1, y - 1, BAR_WIDTH + 2, BAR_HEIGHT + 2);
    ctx.globalAlpha = 1;

    // Fill
    const fillColor =
      this.unit.team === "player"
        ? ratio > 0.4
          ? "#3da35a"
          : "#c89b3c"
        : "#c8413d";
    ctx.fillStyle = fillColor;
    ctx.fillRect(x, y, BAR_WIDTH * ratio, BAR_HEIGHT);

    // Border
    ctx.strokeStyle = "#2a3446";
    ctx.lineWidth = 1;
    ctx.strokeRect(x - 1, y - 1, BAR_WIDTH + 2, BAR_HEIGHT + 2);

    ctx.restore();
  }
}
